// Stops browser page-translation from white-screening the app.
//
// THE FAILURE
//
// Chrome's built-in translation (and Google Translate generally) rewrites text
// nodes in place, replacing them with <font> wrappers of its own. React still
// holds references to the original text nodes. The next time React tries to
// remove or reorder one of them it calls removeChild on a node that is no
// longer its child, the DOM throws
//
//   NotFoundError: Failed to execute 'removeChild' on 'Node':
//   The node to be removed is not a child of this node.
//
// and because that happens inside React's commit phase, the whole tree
// unmounts — the user gets a blank white page and loses whatever they were
// doing. Observed on the cohorts admin page: assigning a gemeente or sending
// a warning email blanked the screen, though the operation itself had already
// succeeded server-side.
//
// WHY GUARD RATHER THAN SUPPRESS TRANSLATION
//
// Adding <meta name="google" content="notranslate"> would fix it in one line,
// and was explicitly rejected: ScConnect is a Dutch application whose
// non-Dutch readers rely on browser translation to use it at all. Taking that
// away to protect the framework is the wrong trade.
//
// WHY GUARD RATHER THAN FIX EVERY COMPONENT
//
// The per-component fix is to never conditionally render a bare text node —
// always wrap it in an element React owns. That is correct, but it has to hold
// for every component in the app forever, including ones not yet written. This
// guard is a floor under all of them.
//
// THE TRADE-OFF, STATED PLAINLY
//
// These two operations become non-throwing when the parent/child relationship
// is already broken. That is a deliberate lie to React: the node it wanted
// gone may stay on screen as stale translated text until the next re-render.
// A slightly stale label beats losing the page. Genuine bugs of this shape
// would also be swallowed, so both paths log to the console rather than
// failing silently.
//
// Reference: facebook/react#11538, open since 2017.

type NodeMethodHost = typeof Node.prototype;

let installed = false;

export const installTranslateCrashGuard = (): void => {
  if (installed) return;
  if (typeof Node !== 'function' || !Node.prototype) return;
  installed = true;

  const proto = Node.prototype as NodeMethodHost;
  const originalRemoveChild = proto.removeChild;
  const originalInsertBefore = proto.insertBefore;

  proto.removeChild = function <T extends Node>(this: Node, child: T): T {
    if (child.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn(
        '[translateCrashGuard] Skipped removeChild for a node whose parent changed — ' +
          'usually page translation rewriting the DOM under React.',
        { child, parent: this }
      );
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  proto.insertBefore = function <T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      // eslint-disable-next-line no-console
      console.warn(
        '[translateCrashGuard] Skipped insertBefore for a reference node whose parent changed — ' +
          'usually page translation rewriting the DOM under React.',
        { newNode, referenceNode, parent: this }
      );
      return newNode;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
};

export default installTranslateCrashGuard;
